import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../../contexts/AuthContext"
import { API_BASE_URL } from "../../../config/api"
import { 
  ChatBubbleLeftRightIcon, 
  CheckCircleIcon,
  ArrowPathIcon,
  CursorArrowRaysIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PaperAirplaneIcon
} from "@heroicons/react/24/outline"

const ConversationReport = () => {
  const navigate = useNavigate()
  const { currentUser, getAllWabaAccounts } = useAuth()
  
  const [stats, setStats] = useState({
    totalToday: 0,
    activeLast24h: 0,
    openCount: 0,
    closedCount: 0,
    total: 0
  })
  
  const [conversations, setConversations] = useState([])
  const [buttonClicks, setButtonClicks] = useState([])
  const [aggregatedButtons, setAggregatedButtons] = useState([])
  
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    senderNumber: "all"
  })
  
  const [wabaPhoneNumbers, setWabaPhoneNumbers] = useState([])
  const [selectedButtonText, setSelectedButtonText] = useState("")
  const [searchQuery, setSearchQuery] = useState("")

  // Load WABA numbers for the dropdown
  useEffect(() => {
    let isMounted = true
    const loadPhones = async () => {
      try {
        if (typeof getAllWabaAccounts === "function") {
          const res = await getAllWabaAccounts()
          if (!isMounted) return
          if (res?.success) {
            const phones = []
            ;(res.accounts || []).forEach((acc) => {
              ;(acc.phoneNumbers || acc.phone_numbers || []).forEach((ph) => {
                const num = ph.display_phone_number || ph.phone_number || ph.number || ""
                const id = ph.id || ph.phone_number_id || ""
                if (num || id) phones.push({ label: num || id, value: num.replace(/\D/g, "") || id })
              })
            })
            if (phones.length === 0) {
              const fallback = currentUser?.cloudNumber || currentUser?.number || ""
              if (fallback) phones.push({ label: fallback, value: fallback.replace(/\D/g, "") })
            }
            setWabaPhoneNumbers(prev => {
              const map = new Map()
              ;[...prev, ...phones].forEach(p => map.set(p.value, p))
              return Array.from(map.values())
            })
          }
        }
      } catch (err) {
        console.error("Error loading WABA phones:", err)
      }
    }
    if (currentUser?.id || currentUser?._id) loadPhones()
    return () => { isMounted = false }
  }, [currentUser, getAllWabaAccounts])

  const fetchData = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token")
      const headers = {
        Authorization: `Bearer ${token}`
      }
      
      const queryParams = new URLSearchParams()
      if (filters.startDate) queryParams.append("startDate", filters.startDate)
      if (filters.endDate) queryParams.append("endDate", filters.endDate)
      if (filters.senderNumber && filters.senderNumber !== "all") queryParams.append("senderNumber", filters.senderNumber)
      
      const qs = queryParams.toString()

      // Fetch Stats & Conversations
      const statsRes = await fetch(`${API_BASE_URL}/api/reports/conversations/stats?${qs}`, { headers })
      if (statsRes.ok) {
        const statsData = await statsRes.json()
        if (statsData.success) {
          setStats(statsData.stats)
          setConversations(statsData.conversations)
          if (Array.isArray(statsData.availableNumbers) && statsData.availableNumbers.length > 0) {
            setWabaPhoneNumbers(prev => {
              const map = new Map()
              ;[...prev, ...statsData.availableNumbers].forEach(p => map.set(p.value, p))
              return Array.from(map.values())
            })
          }
        }
      }

      // Fetch Button Clicks
      const clicksRes = await fetch(`${API_BASE_URL}/api/reports/conversations/button-clicks?${qs}`, { headers })
      if (clicksRes.ok) {
        const clicksData = await clicksRes.json()
        if (clicksData.success) {
          setButtonClicks(clicksData.buttonClicks)
          setAggregatedButtons(clicksData.aggregatedButtons)
          if (Array.isArray(clicksData.availableNumbers) && clicksData.availableNumbers.length > 0) {
            setWabaPhoneNumbers(prev => {
              const map = new Map()
              ;[...prev, ...clicksData.availableNumbers].forEach(p => map.set(p.value, p))
              return Array.from(map.values())
            })
          }
        }
      }

    } catch (error) {
      console.error("Error fetching conversation report:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  // Filtered button clicks based on button text selection & search query
  const filteredButtonClicks = useMemo(() => {
    return buttonClicks.filter(click => {
      const matchButton = !selectedButtonText || click.buttonText === selectedButtonText
      const q = searchQuery.toLowerCase().trim()
      const matchSearch = !q || 
        (click.contactName && click.contactName.toLowerCase().includes(q)) ||
        (click.contactNumber && click.contactNumber.includes(q)) ||
        (click.buttonText && click.buttonText.toLowerCase().includes(q))
      return matchButton && matchSearch
    })
  }, [buttonClicks, selectedButtonText, searchQuery])

  // Filtered conversations based on search query
  const filteredConversations = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    if (!q) return conversations
    return conversations.filter(conv => 
      (conv.contactName && conv.contactName.toLowerCase().includes(q)) ||
      (conv.contactNumber && conv.contactNumber.includes(q)) ||
      (conv.lastMessagePreview && conv.lastMessagePreview.toLowerCase().includes(q))
    )
  }, [conversations, searchQuery])

  return (
    <div className="p-6 w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-600 to-teal-600 dark:from-green-400 dark:to-teal-400 flex items-center gap-3">
            <ChatBubbleLeftRightIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
            Conversation Report
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400 text-sm">
            Detailed metrics for conversations, replies, and interactive button clicks.
          </p>
        </div>

        {/* Global Search Bar */}
        <div className="relative min-w-[280px]">
          <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search contact, number, button..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-8 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500 shadow-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
            Start Date
          </label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange("startDate", e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
            End Date
          </label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange("endDate", e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white text-sm"
          />
        </div>
        <div className="w-56">
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
            Select Number
          </label>
          <select
            value={filters.senderNumber}
            onChange={(e) => handleFilterChange("senderNumber", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white text-sm font-medium"
          >
            <option value="all">📱 All Numbers</option>
            {wabaPhoneNumbers.map((ph, idx) => (
              <option key={idx} value={ph.value}>
                📞 {ph.label}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={fetchData}
          className="ml-auto flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-5 py-2.5 rounded-xl shadow-md transition-all font-medium text-sm"
        >
          <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col items-center text-center hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center mb-3">
            <ChatBubbleLeftRightIcon className="w-6 h-6" />
          </div>
          <h3 className="text-gray-500 dark:text-gray-400 text-xs font-semibold uppercase tracking-wider">Total Chats Today</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.totalToday}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col items-center text-center hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mb-3">
            <ArrowPathIcon className="w-6 h-6" />
          </div>
          <h3 className="text-gray-500 dark:text-gray-400 text-xs font-semibold uppercase tracking-wider">Replies Last 24h</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.activeLast24h}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col items-center text-center hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-full flex items-center justify-center mb-3">
            <CheckCircleIcon className="w-6 h-6" />
          </div>
          <h3 className="text-gray-500 dark:text-gray-400 text-xs font-semibold uppercase tracking-wider">Open Conversations</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.openCount}</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col items-center text-center hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full flex items-center justify-center mb-3">
            <CheckCircleIcon className="w-6 h-6" />
          </div>
          <h3 className="text-gray-500 dark:text-gray-400 text-xs font-semibold uppercase tracking-wider">Closed Conversations</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{stats.closedCount}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Button Clicks Stats */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden flex flex-col">
          <div className="p-5 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <CursorArrowRaysIcon className="w-5 h-5 text-indigo-500" />
              Button Click Stats
            </h2>
            {selectedButtonText && (
              <button
                onClick={() => setSelectedButtonText("")}
                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 font-medium"
              >
                <span>Clear filter</span>
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="p-5 overflow-x-auto flex-1 max-h-[380px] overflow-y-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b dark:border-gray-700">
                  <th className="pb-3 px-3">Button Text</th>
                  <th className="pb-3 px-3 text-right">Clicks</th>
                  <th className="pb-3 px-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {aggregatedButtons.map((btn, i) => {
                  const isSelected = selectedButtonText === btn.buttonText
                  return (
                    <tr 
                      key={i} 
                      onClick={() => setSelectedButtonText(isSelected ? "" : btn.buttonText)}
                      className={`cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-indigo-50 dark:bg-indigo-900/30' 
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <td className="py-3 px-3 text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                        {btn.buttonText}
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-700 dark:text-gray-300 text-right font-bold">
                        <span className="px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-xs">
                          {btn.count}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span className={`text-xs font-medium px-2 py-1 rounded-md ${
                          isSelected 
                            ? 'bg-indigo-600 text-white' 
                            : 'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950'
                        }`}>
                          {isSelected ? 'Filtered' : 'Filter'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
                {aggregatedButtons.length === 0 && (
                  <tr>
                    <td colSpan="3" className="py-12 text-center text-gray-500 text-sm">
                      No button clicks found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Button Clicks Log */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden flex flex-col">
          <div className="p-5 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <MagnifyingGlassIcon className="w-5 h-5 text-indigo-500" />
              Recent Button Clicks Activity
              {selectedButtonText && (
                <span className="ml-2 text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300 px-2.5 py-0.5 rounded-full font-medium">
                  {selectedButtonText}
                </span>
              )}
            </h2>
            <span className="text-xs text-gray-500 font-medium">
              {filteredButtonClicks.length} {filteredButtonClicks.length === 1 ? 'click' : 'clicks'}
            </span>
          </div>
          <div className="p-5 overflow-x-auto flex-1 max-h-[380px] overflow-y-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b dark:border-gray-700">
                  <th className="pb-3 px-3">User Name</th>
                  <th className="pb-3 px-3">Number</th>
                  <th className="pb-3 px-3">Button Clicked</th>
                  <th className="pb-3 px-3">Time</th>
                  <th className="pb-3 px-3 text-right">Chat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredButtonClicks.map((click, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="py-3 px-3 text-sm font-medium text-gray-900 dark:text-white">
                      {click.contactName}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 dark:text-gray-400 font-mono">
                      {click.contactNumber}
                    </td>
                    <td className="py-3 px-3 text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-900/30">
                        {click.buttonText}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {new Date(click.clickedAt).toLocaleString()}
                    </td>
                    <td className="py-3 px-3 text-right">
                      <button
                        onClick={() => navigate('/chat', { state: { selectedContact: click.contactNumber } })}
                        title="Open Chat"
                        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                      >
                        <PaperAirplaneIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredButtonClicks.length === 0 && (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-gray-500 text-sm">
                      {selectedButtonText ? `No clicks found for "${selectedButtonText}"` : "No recent activity"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Full Conversation Log */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="p-5 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Full Conversation Log</h2>
            <p className="text-xs text-gray-500 mt-0.5">Real-time log of customer conversations and latest messages</p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
            {filteredConversations.length} Conversations
          </span>
        </div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 border-b border-gray-200 dark:border-gray-700 z-10">
              <tr className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <th className="p-4">Contact</th>
                <th className="p-4">Business No.</th>
                <th className="p-4">Status</th>
                <th className="p-4">Unread</th>
                <th className="p-4">Last Message</th>
                <th className="p-4">Time</th>
                <th className="p-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredConversations.map((conv, i) => {
                const contactNumber = conv.contactNumber || (conv.senderId && conv.senderId !== conv.businessNumber ? conv.senderId : conv.receiverId)
                return (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="p-4">
                      <div className="font-semibold text-gray-900 dark:text-white">{conv.contactName || contactNumber || "Unknown"}</div>
                      <div className="text-xs text-gray-500 font-mono mt-0.5">{contactNumber}</div>
                    </td>
                    <td className="p-4 text-sm text-gray-600 dark:text-gray-300 font-mono">{conv.businessNumber}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        conv.isArchived 
                          ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' 
                          : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400'
                      }`}>
                        {conv.isArchived ? "Closed" : "Open"}
                      </span>
                    </td>
                    <td className="p-4 text-sm">
                      {conv.unreadCount > 0 ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-red-100 text-red-800 rounded-full text-xs font-bold">
                          {conv.unreadCount}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                      {conv.lastMessagePreview || "No message content"}
                    </td>
                    <td className="p-4 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {new Date(conv.lastMessageAt).toLocaleString()}
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => navigate('/chat', { state: { selectedContact: contactNumber } })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-300 text-xs font-medium rounded-lg transition-colors shadow-sm"
                      >
                        <PaperAirplaneIcon className="w-3.5 h-3.5" />
                        Chat
                      </button>
                    </td>
                  </tr>
                )
              })}
              {filteredConversations.length === 0 && (
                <tr>
                  <td colSpan="7" className="p-12 text-center text-gray-500">
                    {searchQuery ? `No conversations matching "${searchQuery}"` : "No conversations found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default ConversationReport
